import React, { useEffect, useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../config/firebase';
import {
  MOBILE_FLAGS_DOC,
  MOBILE_LIVE,
  MOBILE_WAITLIST,
  MOBILE_SCREEN_GROUPS,
  mobileFlagValue,
} from '../../utils/mobileFeatureFlags';

function Segment({ label, value, onChange, disabled, colors }) {
  const options = [
    { key: MOBILE_LIVE, label: 'Live' },
    { key: MOBILE_WAITLIST, label: 'Waitlist' },
  ];
  return (
    <View
      accessibilityRole="radiogroup"
      accessibilityLabel={`${label} in the app`}
      style={{ flexDirection: 'row', borderRadius: 8, borderWidth: 1, borderColor: colors.cardBorder, overflow: 'hidden' }}
    >
      {options.map((opt) => {
        const selected = value === opt.key;
        return (
          <Pressable
            key={opt.key}
            accessibilityRole="radio"
            accessibilityLabel={`${label}: ${opt.label}`}
            accessibilityState={{ checked: selected, disabled }}
            disabled={disabled}
            onPress={() => !selected && onChange(opt.key)}
            style={{
              paddingVertical: 6,
              paddingHorizontal: 12,
              minWidth: 72,
              alignItems: 'center',
              backgroundColor: selected ? (opt.key === MOBILE_LIVE ? '#2E7D63' : '#B4580E') : 'transparent',
            }}
          >
            <Text style={{ fontSize: 13, fontWeight: '600', color: selected ? '#ffffff' : colors.textSecondary }}>
              {opt.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/** Live / Waitlist per native-app screen. The app listens to the doc, so changes show without a release. */
export function AdminMobileFeatureFlags({ adminStyles, colors, setNotification }) {
  const [flags, setFlags] = useState({});
  const [saving, setSaving] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => onSnapshot(
    doc(db, ...MOBILE_FLAGS_DOC),
    (snap) => {
      setFlags(snap.exists() ? snap.data() : {});
      setLoaded(true);
    },
    (err) => {
      console.error('mobileFeatureFlags', err);
      setNotification?.({ message: 'Could not load app screen flags', type: 'error' });
    },
  ), []);

  const save = async (id, value) => {
    setSaving(id);
    try {
      await setDoc(doc(db, ...MOBILE_FLAGS_DOC), {
        [id]: value,
        updatedAt: serverTimestamp(),
        updatedBy: getAuth().currentUser?.uid || null,
      }, { merge: true });
      setNotification?.({ message: 'App screen flag saved', type: 'success' });
    } catch (e) {
      console.error(e);
      setNotification?.({ message: e.message || 'Failed to save flag', type: 'error' });
    } finally {
      setSaving(null);
    }
  };

  return (
    <View style={{ marginTop: 28 }}>
      <Text style={[adminStyles.cardTitle, { fontSize: 18 }]}>Mobile app screens</Text>
      <Text style={{ color: colors.textSecondary, fontSize: 13, lineHeight: 19, marginTop: 6, marginBottom: 14 }}>
        Live shows the screen in the iOS and Android app. Waitlist shows a "Notify me" page instead; signups land in
        Notify me as "App: …". Changes reach open apps within seconds, no release needed. Screens marked
        "Not in the app yet" show the waitlist until a release ships them.
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
        {MOBILE_SCREEN_GROUPS.map((group) => (
          <View key={group.label} style={[adminStyles.devModeSection, { flex: 1, minWidth: 300, marginTop: 0, marginBottom: 0 }]}>
            <Text style={[adminStyles.cardTitle, { fontSize: 16 }]}>{group.label}</Text>
            {group.screens.map((screen) => (
              <View
                key={screen.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  paddingVertical: 10,
                  borderTopWidth: 1,
                  borderTopColor: colors.cardBorder,
                }}
              >
                <View style={{ flexShrink: 1 }}>
                  <Text style={{ color: colors.textPrimary, fontWeight: '600', fontSize: 14 }}>{screen.label}</Text>
                  {!screen.built ? (
                    <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 2 }}>Not in the app yet</Text>
                  ) : null}
                </View>
                <Segment
                  label={screen.label}
                  value={mobileFlagValue(flags, screen)}
                  onChange={(v) => save(screen.id, v)}
                  disabled={!loaded || saving === screen.id}
                  colors={colors}
                />
              </View>
            ))}
          </View>
        ))}
      </View>
    </View>
  );
}
